import { StrictMode } from 'react'
import { initSentry } from '@/core/sentry'
import { initAnalytics } from '@/core/analytics'
import { registerSwUpdater, markSwWaiting } from '@/core/firebase/version'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { App } from '@/App'
import { ErrorBoundary } from '@/shared/components/error-boundary'
import './index.css'

// Build-target sentinel. vite.config.ts has defined `__BUILD_TARGET_MARKER__`
// since this app existed, but NOTHING referenced it — and a vite `define` only
// substitutes where the identifier appears, so the marker never entered a
// bundle. The prod deploy guard fell back to grepping `return"prod"`, a
// minifier artifact of `resolveEnv()` that would break silently on an esbuild
// output change or a refactor of that function.
// Assigned to globalThis, not exported, because a side effect is the only form
// Rollup cannot tree-shake. Same two lines as clubmanager's main.tsx, which had
// this right from the start.
declare const __BUILD_TARGET_MARKER__: string
;(globalThis as { __uPadelMatchBuildTarget__?: string }).__uPadelMatchBuildTarget__ =
  __BUILD_TARGET_MARKER__

// The commit this bundle was built from. Same side-effect assignment for the
// same reason as the marker above — a `define` that nothing references never
// reaches the bundle. The prod deploy reads this back off DEV hosting to prove
// the code ran on dev before it reaches players (`verify-dev-ran-this.mjs`).
declare const __BUILD_COMMIT__: string
;(globalThis as { __uPadelMatchBuildCommit__?: string }).__uPadelMatchBuildCommit__ =
  __BUILD_COMMIT__

initSentry()
void initAnalytics()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, gcTime: 5 * 60_000, retry: 1, refetchOnWindowFocus: false },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found')

createRoot(rootEl).render(
  <StrictMode>
    {/* Outermost so nothing can escape it — including a throw inside the router
        itself or a provider. Before this the app had no boundary at all: any
        render error was a blank page that survived reload, because the service
        worker re-served the same broken shell. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)

// Register the service worker in production only (mirrors clubmanager — avoids
// SW churn in dev). 'prompt' registration: no auto-reload loop.
if (import.meta.env.PROD) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    // `onNeedRefresh` was being discarded. It is the only signal that is true
    // whenever a new bundle is actually waiting — the Firestore version check
    // in version.ts cannot see a redeploy under the same version number, and
    // 'prompt' registration never activates on its own, so without this a
    // returning visitor keeps their cached shell forever.
    registerSwUpdater(registerSW({ immediate: false, onNeedRefresh: markSwWaiting }))
  })
}
